// Imports
import { AppBar, Drawer, FormControl, IconButton, List, ListItem, ListItemIcon, ListItemText, Select, Toolbar, Typography, MenuItem, useTheme, useMediaQuery } from "@mui/material";
import { GridView, ListAltOutlined, LocalOfferOutlined, LockOutlined, MenuBookOutlined, MopedOutlined, SportsMotorsportsOutlined, Menu as MenuIcon, AccountCircle, Logout, BarChart, Reviews, SupportAgent } from '@mui/icons-material';
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getNavSelection, setNavSelection } from '../../features/navSelection'
import { getHubSelection, setHubSelection } from '../../features/hubSelection'
import { getVendorHubs, getVendorProfile, setVendorLoginStatus } from '../../features/vendorProfile'
import { Colors, NavTabIndices } from '../../config/Constants';
import { useCookies } from 'react-cookie';
import './navbar.css';
import { Link, useLocation } from "react-router-dom";

// Static data for toolbar
const toolbarItems: { name: string, icon: JSX.Element, index: NavTabIndices, link: string, hidden?: boolean, hideOnPC?: boolean }[] = [
  { name: 'Home', link: '/', icon: <GridView fontSize="large" />, index: NavTabIndices.HOME, hideOnPC: true },
  { name: 'Analytics', link: '/dashboard', icon: <BarChart fontSize="large" />, index: NavTabIndices.DASHBOARD },
  { name: 'Bookings', link: '/bookings', icon: <MenuBookOutlined fontSize="large" />, index: NavTabIndices.BOOKINGS },
  { name: 'Reviews', link: '/reviews', icon: <Reviews fontSize="large" />, index: NavTabIndices.REVIEWS },
  { name: 'Vehicles', link: '/bikes', icon: <MopedOutlined fontSize="large" />, index: NavTabIndices.BIKES },
  { name: 'Accessories', link: '/accessories', icon: <SportsMotorsportsOutlined fontSize="large" />, index: NavTabIndices.ACCESSORIES },
  { name: 'Block/Unblock', link: '/blocks', icon: <LockOutlined fontSize="large" />, index: NavTabIndices.BLOCKS },
  // { name: 'Unblock Bikes', link: '', icon: <LockOpenOutlined fontSize="large" /> , index: NavTabIndices.DASHBOARD},
  { name: 'Payments', link: '/ledger', icon: <ListAltOutlined fontSize="large" />, index: NavTabIndices.LEDGER },
  { name: 'Offers', link: '/offers', icon: <LocalOfferOutlined fontSize="large" />, index: NavTabIndices.OFFERS },
  { name: 'Support Tickets', link: '/tickets', icon: <SupportAgent fontSize="large" />, index: NavTabIndices.TICKETS },
  { name: 'Profile', link: '/profile', icon: <AccountCircle fontSize="large" />, index: NavTabIndices.PROFILE },
]

// Navbar component
function Navbar(props: {
  window?: () => Window;
  drawerWidth: number
}) {

  // Parse props
  const { window, drawerWidth } = props;

  // Local state
  const [mobileOpen, setMobileOpen] = useState(false);
  const [, , removeCookie] = useCookies(['vendor-token']);

  // Global state and constants
  const navSelectionState = useSelector(getNavSelection);
  const hubSelectionState = useSelector(getHubSelection);
  const hubList = useSelector(getVendorHubs);
  const profile = useSelector(getVendorProfile);
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up('md'));

  // Set nav selection on load
  useEffect(() => {
    dispatch(setNavSelection(toolbarItems.findIndex(i => i.link === location.pathname)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create drawer content
  const drawer = (
    <div className="gobikes-nav">

      {/* Logo */}
      <Toolbar sx={{ background: Colors.primary }}>
        <img className="toolbar-logo" src="/logo-gowheelo.png" alt="gowheelo-logo" />
      </Toolbar>

      {/* Hub selector */}
      {hubList.length > 1 ? (
        <Box sx={{ minWidth: 100 }}>
          <FormControl sx={{ margin: { xs: '10px 15%', md: '30px 15%' }, width: '70%' }} >
            <Select
              labelId="hub-selection-label"
              id="hub-selection"
              value={hubSelectionState?.id || 0}
              onChange={(a) => { dispatch(setHubSelection(hubList.find(hub => hub.id === a.target.value))) }}
              sx={{ background: Colors.primaryDark }}
              className="hub-select"
              size={isPhone ? 'small' : 'medium'}
            >
              {hubList.map((item, index) => (
                <MenuItem value={item.id} key={item.id}>{item.landmark}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ) : ''}

      {/* Nav list */}
      <List sx={{ background: Colors.primary }}>
        {toolbarItems.filter(i => !(i.hidden || (!isPhone && i.hideOnPC))).map((item: { name: string, link: string, icon: JSX.Element, index: NavTabIndices, }, index) => (
          <Link key={item.name} to={item.link}>
            <ListItem
              button
              key={item.name}
              sx={{ background: item.index === navSelectionState ? Colors.primaryDark : Colors.primary }}
              className={`toolbar-item ${item.index === navSelectionState ? 'active' : ''}`}
              onClick={() => {
                dispatch(setNavSelection(item.index));
                if (mobileOpen) setMobileOpen(!mobileOpen);
              }}
            >
              <ListItemIcon className="toolbar-icon"> {item.icon} </ListItemIcon>
              <ListItemText className="toolbar-text" primary={item.name} />
            </ListItem>
          </Link>

        ))}
      </List>

      {/* Logout button */}
      <div
        className="logout-button"
        onClick={() => {
          localStorage.removeItem('vendor-token');
          removeCookie('vendor-token');
          dispatch(setVendorLoginStatus(false));
        }}>
        <span className="gbk-icon-span" style={{ color: 'white', lineHeight: '0px', display: 'inline-block' }}><Logout /></span>
        <Typography fontSize={40}>Sign Out</Typography>
      </div>

    </div>
  );

  // Create navbar which contains drawer
  const container = window !== undefined ? () => window().document.body : undefined;
  return (
    <Box sx={{ display: 'flex' }}>

      {/* Appbar for opening navbar in phone */}
      <AppBar
        position="fixed"
        sx={{
          display: { md: 'none' },
          backgroundColor: 'white',
          boxShadow: '0px 1px 4px rgba(0,0,0,0.1)',
        }} >
        <Toolbar className="app-bar">
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }} >
            <MenuIcon fontSize="large" sx={{ color: Colors.primary }} />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ fontSize: '17px' }}>
            {toolbarItems?.[navSelectionState]?.name}
          </Typography>
          {navSelectionState === NavTabIndices.DASHBOARD ? (
            <Box sx={{ ml: 'auto', textAlign: 'right' }}>
              <Typography noWrap component="div" className="app-bar-balance-text" sx={{ fontSize: '10px', fontWeight: 600, color: Colors.primary, lineHeight: 1.4 }}>
                Balance
              </Typography>
              <Typography noWrap component="div" className="app-bar-balance-text" sx={{ fontSize: '15px', fontWeight: 600, color: Colors.primary }}>
                <span className="roboto">₹</span> {(profile.cashfreeData?.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
          ) : ''}
        </Toolbar>
      </AppBar>

      {/* Actual navbar */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders">

        {/* Phone navbar */}
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, backgroundColor: Colors.primary },
          }} >
          {drawer}
        </Drawer>

        {/* PC navbar */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, backgroundColor: Colors.primary },
            height: '100%'
          }}
          open >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  );

  // Handle opening and closing of drawer
  function handleDrawerToggle(): void {
    setMobileOpen(!mobileOpen);
  };

}
export default Navbar;